/**
 * Procedural per-floor layout on a 40×40-stud grid over the arena interior.
 * Seeded by the layer number (identical on resume).
 *
 * GRID QUANTITY, NOT TILE SIZE. Every floor uses the SAME 40-stud cell; bigger
 * floors get MORE cells (the grid grows in cols/rows with the layer), so high
 * layers become large, intricate mazes rather than the same maze with bigger
 * tiles. The grid is WORLD-NATIVE: a cell is 40 world studs, the grid is centred
 * on the origin and spans the interior EXACTLY (interior = cols·CELL × rows·CELL),
 * so it lines up with the perimeter walls with no leftover gap — no "caps" patch.
 *
 * The floor is built from three overlaid kinds of data:
 *
 *   solid[c][r]  — an impassable GROUND maze block (BLOCK_H tall, brown). Joins
 *                  tank collision + shell ricochet on the ground (gamemap DYN_WALLS).
 *   decks[c][r]  — the set of ELEVATED ROAD levels with a deck at this cell. Roads
 *                  are thin slabs you drive ON TOP of and UNDERNEATH (open space
 *                  below). They wind across the floor like overpasses, generated as
 *                  long paths, NOT blobs. Up to TOP_LEVEL stacked levels.
 *   lift[c][r]   — a lift connecting two levels {lo,hi} at this cell. Drive onto it
 *                  and press the ride key to be carried up/down (~1s). Every lift is
 *                  VALIDATED so both ends land on a real surface — never into a block.
 *
 * A valid GROUND path from the spawn hole to the elevator hole is always
 * guaranteed (carved corridor + reachability check), so every floor is winnable
 * on the ground alone; the roads are optional verticality / shortcuts / escape.
 *
 * Movement, shells and the enemy flow-field are all LEVEL-AWARE: a tank/shell is
 * on a discrete level (0 = ground … TOP_LEVEL); it only collides with / is hit by
 * things on its own level. The flow field is a BFS over the SURFACE GRAPH (ground
 * cells + deck cells + lift edges), so enemies follow the player up and down.
 */

import * as THREE from "three";

export interface AABB { minX: number; maxX: number; minZ: number; maxZ: number; wedge?: number; nx?: number; nz?: number; water?: boolean }

/** What gamemap hands the arena: the world-native grid size and the two holes
 *  (their world centres + the world pit AABBs the maze must keep clear). */
export interface ArenaLayout {
  cols: number;
  rows: number;
  spawn: { cx: number; cz: number };
  elev: { cx: number; cz: number };
  spawnHole: AABB; // the pit (the actual opening)
  elevHole: AABB;
  spawnClear: AABB; // the full hole+curb footprint (scaled) — keep the maze off it
  elevClear: AABB;
}

export const CELL = 40;

/** Small deterministic RNG (mulberry32) seeded per layer for resumable layouts. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ground maze blocks + curbs are the height of the hole curbs (10 studs).
export const BLOCK_H = 10;
// the floor above sits on the perimeter walls (WALL_TOP); a road deck must not
// poke through it. WALL_TOP − a margin caps the top level's height.
export const WALL_TOP = 170;
// height between road levels. Tall enough to drive a tank UNDER a deck with room
// to spare (tank ≈ 9 tall, deck DECK_T thick → clearance LEVEL_H − DECK_T ≈ 29).
export const LEVEL_H = 34;
const DECK_T = 5; // road deck slab thickness
export const TOP_LEVEL = 3; // ground + 3 stacked road levels (0,34,68,102 ≤ WALL_TOP)
const LIFT_COST = 4; // a lift "costs" this many cells of travel in the flow field
const CLEAR_PAD = CELL * 1.4; // flat landing ring kept clear AROUND each hole pit

// Materials + geometries below are module-level and shared by every layer's
// InstancedMeshes for the whole session — intentionally never disposed (a handful
// of small untextured resources; Arena.dispose only unlinks the per-layer group).
const obstacleMat = new THREE.MeshLambertMaterial({ color: 0x6e4a26 }); // brown = impassable ground wall
// slate road decks, tinted slightly per level + semi-transparent so the ground
// (and tanks driving underneath) reads through them from the top-down camera.
// depthWrite stays OFF (these are see-through): instead each level's deck is given an
// explicit renderOrder (= level, below the UI overlays) so higher roads always draw
// OVER lower ones deterministically — fixing the wrong cross-level sort without the deck
// writing depth (which would wrongly occlude shields/labels/rings/aim-line/lasers).
const deckMats = [0x8893a3, 0x7a8595, 0x6d7787].map((c) =>
  new THREE.MeshLambertMaterial({ color: c, transparent: true, opacity: 0.62, depthWrite: false, side: THREE.DoubleSide }));
const liftDeckMat = new THREE.MeshLambertMaterial({ color: 0x39d98a, transparent: true, opacity: 0.7, depthWrite: false, side: THREE.DoubleSide });
const liftPadMat = new THREE.MeshLambertMaterial({ color: 0x39d98a, emissive: 0x0d5a38 });
const postMat = new THREE.MeshLambertMaterial({ color: 0x2fa872 });

// Shared geometries reused by every layer's InstancedMeshes. Allocating a box per
// cell (as before) ballooned draw calls AND leaked geometry on each floor change.
const SOLID_GEOM = new THREE.BoxGeometry(CELL - 1, BLOCK_H, CELL - 1);
const DECK_GEOM = new THREE.BoxGeometry(CELL, DECK_T, CELL);
const PAD_GEOM = new THREE.BoxGeometry(CELL - 8, 1.5, CELL - 8);
const POST_GEOM = new THREE.BoxGeometry(1, 1, 1); // scaled per-instance to the shaft height

// WEDGE: 4 right-triangle prisms (one per orientation), built from a 2D triangle extruded UP.
// Shape is in XY extruded along +Z, then rotated so +Z→+Y → a triangle footprint, BLOCK_H tall.
function makeWedge(a: [number, number], b: [number, number], c: [number, number]): THREE.BufferGeometry {
  const s = new THREE.Shape();
  s.moveTo(a[0], -a[1]); s.lineTo(b[0], -b[1]); s.lineTo(c[0], -c[1]); s.closePath(); // -y so XY→XZ keeps winding
  const g = new THREE.ExtrudeGeometry(s, { depth: BLOCK_H, bevelEnabled: false });
  g.rotateX(-Math.PI / 2); // extrude axis (Z) → world up (Y)
  return g;
}
const WH = (CELL - 1) / 2; // visual half-cell (a 1-stud gap, matching SOLID_GEOM)
const WEDGE_GEOM: THREE.BufferGeometry[] = [
  makeWedge([-WH, -WH], [WH, -WH], [-WH, WH]), // 0: solid corner −X,−Z
  makeWedge([WH, -WH], [-WH, -WH], [WH, WH]),  // 1: +X,−Z
  makeWedge([WH, WH], [-WH, WH], [WH, -WH]),   // 2: +X,+Z
  makeWedge([-WH, WH], [-WH, -WH], [WH, WH]),  // 3: −X,+Z
];

// WATER: a flat animated surface tile (cartoon flow via a world-XZ wave shader so it's seamless
// across cells). Blocks tanks (a movement-only AABB), shells fly over it.
// WATER: a merged, LIT surface skin (one BufferGeometry per layer, disposed with the
// arena). Each vertex carries `aShore` = distance-to-shore in world studs, which drives a
// stepped shoreline gradient (foam → shallow → mid → deep) like a cartoon beach. It's a
// MeshStandardMaterial — so the day/night sun, ambient and FOG tint it exactly like every
// other surface (no more night-time "glow") — with onBeforeCompile painting the procedural
// water into diffuseColor before lighting. Vertices hold absolute world XZ, so the drifting
// ripples + meandering bands stay seamless across cell boundaries.
// Smooth, non-periodic value noise — wobbles the water's edge off the perfect cell grid.
function vhash(i: number, j: number): number { const h = Math.sin(i * 127.1 + j * 311.7) * 43758.5453; return h - Math.floor(h); }
function vnoise(x: number, z: number): number {
  const xi = Math.floor(x), zi = Math.floor(z), xf = x - xi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = zf * zf * (3 - 2 * zf); // smoothstep fade
  const a = vhash(xi, zi), b = vhash(xi + 1, zi), c = vhash(xi, zi + 1), d = vhash(xi + 1, zi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}
const WATER_WOBBLE = 11; // studs the visual shoreline may stray from the square grid (collision stays square)
const WOBBLE_FREQ = 1 / 68; // noise wavelength ≈ 68 studs — a gentle, non-repeating meander
/** Smooth XZ offset for a water vertex at world (wx,wz). Keyed purely to the grid NODE, so every
 *  quad sharing a corner displaces it identically → the mesh stays watertight (no cracks). */
function waterWobble(wx: number, wz: number, out: [number, number]): void {
  out[0] = (vnoise(wx * WOBBLE_FREQ + 11.3, wz * WOBBLE_FREQ + 5.1) * 2 - 1) * WATER_WOBBLE;
  out[1] = (vnoise(wx * WOBBLE_FREQ + 71.7, wz * WOBBLE_FREQ + 39.2) * 2 - 1) * WATER_WOBBLE;
}

const waterTime = { value: 0 };
const waterMat = new THREE.MeshStandardMaterial({
  color: 0xffffff, roughness: 0.74, metalness: 0.0,
  transparent: true, depthWrite: false, side: THREE.DoubleSide,
});
waterMat.onBeforeCompile = (shader) => {
  shader.uniforms.uTime = waterTime;
  shader.vertexShader = shader.vertexShader
    .replace("#include <common>", "#include <common>\n attribute float aShore;\n varying float vShore;\n varying vec2 vWXZ;")
    .replace("#include <begin_vertex>", "#include <begin_vertex>\n vShore = aShore;\n vWXZ = (modelMatrix * vec4(transformed, 1.0)).xz;");
  shader.fragmentShader = shader.fragmentShader
    .replace("#include <common>", "#include <common>\n uniform float uTime;\n varying float vShore;\n varying vec2 vWXZ;")
    .replace("#include <color_fragment>", `
      vec2 p = vWXZ;
      // meandering waterline: wobble the depth so the colour bands ripple along the coast
      float wob = sin(p.x * 0.058 + uTime * 0.95) * 2.6 + sin(p.y * 0.050 - uTime * 0.75) * 2.6;
      float sig = vShore + wob; // SIGNED shore distance: <0 just past the waterline → rounded, soft edge
      float s = max(0.0, sig);
      vec3 cFoam    = vec3(0.80, 0.95, 0.99);
      vec3 cShallow = vec3(0.30, 0.72, 0.90);
      vec3 cMid     = vec3(0.11, 0.48, 0.78);
      vec3 cDeep    = vec3(0.05, 0.28, 0.56);
      // distinct STEPPED bands (tight transitions, flat between) like a cartoon shoreline
      vec3 wcol = cFoam;
      wcol = mix(wcol, cShallow, smoothstep(2.0, 6.0, s));
      wcol = mix(wcol, cMid,     smoothstep(11.0, 15.0, s));
      wcol = mix(wcol, cDeep,    smoothstep(24.0, 30.0, s));
      // a few soft wave fronts drifting PARALLEL to the shore (contour lines in the depth field)
      float wf = fract(s * 0.075 - uTime * 0.7);
      wcol += smoothstep(0.78, 0.96, wf) * 0.06;
      // crisp foam lip hugging the very edge of the water
      wcol = mix(wcol, vec3(0.95, 1.0, 1.0), smoothstep(3.0, 0.0, s) * 0.6);
      diffuseColor.rgb = wcol;
      // feather EVERY edge: alpha ramps up from the waterline (vShore≈0) inward, so straight
      // edges, corners and river ends all dissolve softly into the bank — never a hard cutoff.
      // The per-cell CENTRE vertex keeps thin rivers' middles well above 0, so they stay solid.
      float edge = smoothstep(0.0, 7.0, vShore);
      diffuseColor.a = mix(0.74, 0.97, smoothstep(0.0, 20.0, s)) * edge;`);
};

// 4-neighbour offsets for the BFS relaxers (hoisted so the inner loop allocates
// nothing per node — matters on big floors).
const ORTHO: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];

// neighbour offsets for the flow-field step (hoisted so it isn't re-allocated for
// every enemy every frame). [dc, dr, orthoDC, orthoDR]; diagonals check the two
// shared orthogonal cells (c+orthoDC, r) and (c, r+dr) to avoid cutting a corner.
const FLOW_NB: ReadonlyArray<readonly [number, number, number, number]> = [
  [1, 0, 1, 0], [-1, 0, -1, 0], [0, 1, 0, 1], [0, -1, 0, -1],
  [1, 1, 1, 0], [-1, -1, -1, 0], [1, -1, 1, 0], [-1, 1, -1, 0],
];

export interface Lift { lo: number; hi: number }

/** Result of a flow-field query: drive toward (dx,dz), ride a lift, or nothing. */
export interface FlowStep { kind: "none" | "move" | "lift"; dx: number; dz: number; toLevel: number }

export class Arena {
  // grid dimensions are PER-FLOOR now (constant CELL, growing quantity).
  readonly cols: number;
  readonly rows: number;
  private readonly gx0: number; // world X of the grid's left edge (centred on origin)
  private readonly gz0: number; // world Z of the grid's top edge

  private solid: boolean[][] = []; // impassable ground maze block
  private wedge: (number | null)[][] = []; // per solid cell: a wedge orientation 0-3 (right-triangle), or null = full block
  private water = new Set<number>(); // floor-feature cells: block tank movement, shells pass over (rivers)
  private waterGeom: THREE.BufferGeometry | null = null; // per-layer merged water mesh (disposed in dispose())
  private decks: number[][][] = []; // per cell: sorted list of elevated road levels
  private lift: (Lift | null)[][] = []; // per cell: a lift {lo,hi} or null
  private reach: boolean[][] = []; // ground reachable (via the surface graph) from spawn
  private holeCell = new Set<number>(); // cells over a pit (nav can't stand here)
  private noNav = new Set<number>(); // cells inside a hole+curb box — impassable to pathfinding (the curb is a wall)
  private keepClear = new Set<number>(); // flat landing zone around the holes
  private placedLifts = new Set<number>(); // lift cells (for spacing — no clustering)
  readonly walls: AABB[] = []; // solid-ground-block AABBs in WORLD coords (→ gamemap DYN_WALLS)
  readonly group = new THREE.Group();
  private spawnCell: { c: number; r: number };
  private exitCell: { c: number; r: number };
  // landing cells just outside each hole — the flow field / reachability seed here.
  private spawnLanding: Array<{ c: number; r: number }> = [];
  private exitLanding: Array<{ c: number; r: number }> = [];

  /** seed for the (resumable) enemy-spawn-point shuffle. */
  private readonly spawnSeed: number;
  /** this floor forces the player onto a road (ground corridor is walled off). */
  private forced = false;
  /** the bonus-upgrade pickup cell (hard to reach), or null. */
  private pickup: { c: number; r: number; level: number } | null = null;
  private pickupTaken = false;

  // flow fields: multi-source BFS distance over the surface graph, per level grid.
  // `dist` = toward the THREATS enemies hunt (player + mercenaries); `adist` = toward
  // the ENEMIES mercenaries hunt. Both rebuild only when their sources change cells.
  private dist: number[][][] = []; // [level][c][r] — enemy flow (toward threats)
  private adist: number[][][] = []; // ally flow (toward enemies)
  private genDist: number[][][] = []; // static surface distance from spawn (for the pickup)
  private lastGoal = -1;
  private lastAllyGoal = -1;
  private allyFlowCooldown = 0; // frames until the ally flow may rebuild again (throttle)

  constructor(layer: number, layout: ArenaLayout) {
    this.cols = layout.cols;
    this.rows = layout.rows;
    this.gx0 = -(this.cols * CELL) / 2; // grid centred on the origin → spans the interior exactly
    this.gz0 = -(this.rows * CELL) / 2;
    const rnd = rng(layer * 2654435761 + 12345);
    this.spawnSeed = layer * 40503 + 1337;
    for (let c = 0; c < this.cols; c++) {
      this.solid[c] = []; this.wedge[c] = []; this.decks[c] = []; this.lift[c] = []; this.reach[c] = [];
      for (let r = 0; r < this.rows; r++) { this.solid[c][r] = false; this.wedge[c][r] = null; this.decks[c][r] = []; this.lift[c][r] = null; }
    }
    for (let L = 0; L <= TOP_LEVEL; L++) {
      this.dist[L] = []; this.adist[L] = [];
      for (let c = 0; c < this.cols; c++) {
        this.dist[L][c] = []; this.adist[L][c] = [];
        for (let r = 0; r < this.rows; r++) { this.dist[L][c][r] = Infinity; this.adist[L][c][r] = Infinity; }
      }
    }
    // mark the two holes (pit cells + a clear landing ring) and pick the landing
    // cell nearest each hole centre as the spawn/exit anchor for carving + nav.
    this.markHole(layout.spawnHole, layout.spawnClear);
    this.markHole(layout.elevHole, layout.elevClear);
    this.spawnCell = this.nearestCell(layout.spawn.cx, layout.spawn.cz);
    this.exitCell = this.nearestCell(layout.elev.cx, layout.elev.cz);
    this.generate(layer, rnd);
    this.buildMeshes();
  }

  // ---- grid helpers (world-native) --------------------------------------

  cellCenterX(c: number): number { return this.gx0 + (c + 0.5) * CELL; }
  cellCenterZ(r: number): number { return this.gz0 + (r + 0.5) * CELL; }
  cellOf(x: number, z: number): { c: number; r: number } {
    return {
      c: Math.max(0, Math.min(this.cols - 1, Math.floor((x - this.gx0) / CELL))),
      r: Math.max(0, Math.min(this.rows - 1, Math.floor((z - this.gz0) / CELL))),
    };
  }
  private inGrid(c: number, r: number): boolean { return c >= 0 && c < this.cols && r >= 0 && r < this.rows; }
  private key(c: number, r: number): number { return c * this.rows + r; }

  /** Mark hole cells (the pit, where nav can't stand) and the keep-clear zone (the
   *  full hole+curb footprint plus a landing ring) so the maze never overlaps the
   *  elevator/spawn walls. `clear` is the scaled hole+curb box; `pit` is the opening. */
  private markHole(pit: AABB, clear: AABB): void {
    const pad = CLEAR_PAD;
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      const x = this.cellCenterX(c), z = this.cellCenterZ(r);
      if (x >= clear.minX - pad && x <= clear.maxX + pad && z >= clear.minZ - pad && z <= clear.maxZ + pad) this.keepClear.add(this.key(c, r));
      // the hole+curb box itself is impassable to nav — the curb is a real wall the flow
      // field otherwise ignores, so it would route tanks straight onto the elevator.
      if (x >= clear.minX && x <= clear.maxX && z >= clear.minZ && z <= clear.maxZ) this.noNav.add(this.key(c, r));
      if (x >= pit.minX && x <= pit.maxX && z >= pit.minZ && z <= pit.maxZ) this.holeCell.add(this.key(c, r));
    }
  }

  /** The valid ground landing cell nearest a world point (steps out of a pit). */
  private nearestCell(x: number, z: number): { c: number; r: number } {
    const o = this.cellOf(x, z);
    if (!this.holeCell.has(this.key(o.c, o.r))) return o;
    for (let rad = 1; rad < Math.max(this.cols, this.rows); rad++) {
      let best: { c: number; r: number } | null = null, bestD = Infinity;
      for (let dc = -rad; dc <= rad; dc++) for (let dr = -rad; dr <= rad; dr++) {
        const c = o.c + dc, r = o.r + dr;
        if (!this.inGrid(c, r) || this.holeCell.has(this.key(c, r))) continue;
        const d = dc * dc + dr * dr;
        if (d < bestD) { bestD = d; best = { c, r }; }
      }
      if (best) return best;
    }
    return o;
  }

  private addDeck(c: number, r: number, L: number): void { if (!this.decks[c][r].includes(L)) { this.decks[c][r].push(L); this.decks[c][r].sort((a, b) => a - b); } }

  /** A lift may be placed at (c,r) only if no other lift is within 1 cell
   *  (Chebyshev ≥ 2) — keeps lift markers from clustering confusingly. */
  private isLiftSpaced(c: number, r: number): boolean {
    for (let dc = -1; dc <= 1; dc++) for (let dr = -1; dr <= 1; dr++) {
      const nc = c + dc, nr = r + dr;
      if (!this.inGrid(nc, nr)) continue; // key() aliases across row edges — bound first
      if (this.placedLifts.has(this.key(nc, nr))) return false;
    }
    return true;
  }

  /** Can a tank stand here at level L? (ground = non-solid non-hole; else a deck). */
  private valid(c: number, r: number, L: number): boolean {
    if (!this.inGrid(c, r)) return false;
    if (L === 0) return !this.solid[c][r] && !this.holeCell.has(this.key(c, r)) && !this.water.has(this.key(c, r));
    return this.decks[c][r].includes(L);
  }

  // ---- generation -------------------------------------------------------

  private generate(layer: number, rnd: () => number): void {
    const density = Math.min(0.08 + layer * 0.015, 0.32); // a touch lighter — roads add the challenge

    // 1) scatter solid ground maze blocks (never on a hole/landing cell). Some interior blocks
    //    become WEDGES (a sloped right-triangle face — tanks slide it, shells bank off the diagonal).
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      if (this.keepClear.has(this.key(c, r))) continue;
      if (rnd() < density) {
        this.solid[c][r] = true;
        if (layer >= 2 && rnd() < 0.22 && c > 0 && c < this.cols - 1 && r > 0 && r < this.rows - 1) this.wedge[c][r] = (rnd() * 4) | 0;
      }
    }
    // landing cells become reachable now that solids are placed
    this.spawnLanding = this.landingCells(this.spawnCell);
    this.exitLanding = this.landingCells(this.exitCell);

    // 2) carve an initial ground corridor spawn -> exit
    this.carve(this.spawnCell, this.exitCell, rnd);

    // 3) lay winding elevated ROADS. Build bottom-up so higher roads can branch
    //    off lower ones with a lift between the levels.
    const n1 = Math.min(1 + Math.floor(layer / 4), 3 + Math.floor(layer / 12)); // more roads on bigger floors
    for (let i = 0; i < n1; i++) this.buildRoad(1, rnd, null);
    const n2 = layer >= 4 ? Math.min(1 + Math.floor((layer - 4) / 6), 2 + Math.floor(layer / 16)) : 0;
    for (let i = 0; i < n2; i++) this.buildRoad(2, rnd, this.randomDeckCell(1, rnd));
    const n3 = layer >= 9 ? 1 + Math.floor(layer / 20) : 0;
    for (let i = 0; i < n3; i++) this.buildRoad(3, rnd, this.randomDeckCell(2, rnd));

    // 4) on some floors FORCE the road: wall off the ground corridor and bridge
    //    the divide with a road + lifts (you must go up-over-down).
    this.forced = this.shouldForce(layer, rnd);
    if (this.forced) this.buildForcedWallBridge(rnd);

    // 5) every lift must land on a real surface at BOTH ends — drop any "evil"
    //    lift whose destination got buried (e.g. under the forced wall).
    this.validateLifts();

    // 5b) carve WATER rivers (meandering, never straight). Each is provisional: carve → re-check
    //     reachability → revert if it would wall off the exit (so the floor is always solvable).
    this.buildRivers(layer, rnd);

    // 6) connectivity over the full SURFACE graph (ground + decks + lifts). If the
    //    exit can't be reached, carve a clean ground corridor — never a soft-lock.
    this.computeReach();
    if (!this.exitReachable()) {
      this.carve(this.spawnCell, this.exitCell, rnd, true);
      this.forced = false;
      this.computeReach();
    }

    // 7) drop elevated decks that ended up unreachable on the surface graph (e.g. a
    //    road whose only lift was culled) — leftover cosmetic slabs, never a softlock.
    this.pruneOrphanDecks();

    // 8) tuck a bonus-upgrade pickup in the hardest-to-reach reachable spot
    this.selectPickup(rnd);
  }

  /** Valid ground landing cells in a hole's clear ring (the player drives onto
   *  these straight off the platform). */
  private landingCells(anchor: { c: number; r: number }): Array<{ c: number; r: number }> {
    const out: Array<{ c: number; r: number }> = [];
    const reach = Math.ceil(CLEAR_PAD / CELL) + 1;
    for (let dc = -reach; dc <= reach; dc++) for (let dr = -reach; dr <= reach; dr++) {
      const c = anchor.c + dc, r = anchor.r + dr;
      if (this.valid(c, r, 0) && this.keepClear.has(this.key(c, r))) out.push({ c, r });
    }
    return out.length ? out : [anchor];
  }

  /** ~1/3 of floors past the grace period force a road detour. */
  private shouldForce(layer: number, rnd: () => number): boolean {
    return layer >= 4 && rnd() < 0.34;
  }

  /** Wall off the ground midway between the holes and bridge it with a level-1
   *  road carrying lifts on both sides (ground→1 over the wall →ground). The grid
   *  now spans the interior wall-to-wall, so a full column seals it — no caps. */
  private buildForcedWallBridge(rnd: () => number): void {
    const wallC = Math.round((this.spawnCell.c + this.exitCell.c) / 2);
    if (wallC <= 1 || wallC >= this.cols - 1) { this.forced = false; return; }
    // a bridge row (center-out, seeded) whose wall + both shoulders are buildable
    const rows = Array.from({ length: this.rows }, (_, i) => i)
      .sort((a, b) => (Math.abs(a - (this.rows - 1) / 2) - Math.abs(b - (this.rows - 1) / 2)) + (rnd() - 0.5));
    let br = -1;
    for (const rr of rows) {
      if (this.keepClear.has(this.key(wallC, rr))) continue;
      if (this.keepClear.has(this.key(wallC - 1, rr)) || this.keepClear.has(this.key(wallC + 1, rr))) continue;
      // both shoulder lifts must clear the spacing rule, or the bridge can't connect
      if (!this.isLiftSpaced(wallC - 1, rr) || !this.isLiftSpaced(wallC + 1, rr)) continue;
      br = rr; break;
    }
    if (br < 0) { this.forced = false; return; }
    // raise the dividing wall (whole column) — but never bury a landing zone or an
    // existing lift pad (that would strand the player / make an evil elevator).
    for (let r = 0; r < this.rows; r++) {
      if (this.keepClear.has(this.key(wallC, r)) || this.lift[wallC][r]) continue;
      this.solid[wallC][r] = true;
    }
    // bridge deck spanning the wall + a ground lift on each shoulder
    this.addDeck(wallC - 1, br, 1); this.addDeck(wallC, br, 1); this.addDeck(wallC + 1, br, 1);
    this.placeLift(wallC - 1, br, 0, 1);
    this.placeLift(wallC + 1, br, 0, 1);
  }

  /** Drop any lift whose destination isn't a real standable surface at BOTH ends
   *  (so riding it can never drop you into a block — no "evil elevators"). */
  private validateLifts(): void {
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      const lf = this.lift[c][r];
      if (!lf) continue;
      if (!this.valid(c, r, lf.lo) || !this.valid(c, r, lf.hi)) {
        this.lift[c][r] = null;
        this.placedLifts.delete(this.key(c, r));
      }
    }
  }

  /** Pick the hardest-to-reach reachable cell for the bonus pickup (high decks +
   *  ground dead-ends, scored by surface distance from spawn). Deterministic. */
  private selectPickup(rnd: () => number): void {
    const dist = this.genDist;
    const cands: Array<{ c: number; r: number; level: number; score: number }> = [];
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      if (this.keepClear.has(this.key(c, r))) continue;
      // elevated deck cells (skip lift cells — those are thoroughfares)
      if (!this.lift[c][r]) {
        for (const L of this.decks[c][r]) {
          const d = dist[L]?.[c]?.[r];
          if (d !== undefined && isFinite(d)) cands.push({ c, r, level: L, score: d + L * 6 });
        }
      }
      // ground dead-ends (few open neighbours)
      if (this.valid(c, r, 0) && isFinite(dist[0][c][r]) && !this.lift[c][r]) {
        let open = 0;
        for (const [nc, nr] of [[c + 1, r], [c - 1, r], [c, r + 1], [c, r - 1]] as const) if (this.valid(nc, nr, 0)) open++;
        if (open <= 2) cands.push({ c, r, level: 0, score: dist[0][c][r] + (2 - open) * 4 });
      }
    }
    if (!cands.length) { this.pickup = null; return; }
    cands.sort((a, b) => b.score - a.score);
    const top = cands.slice(0, Math.min(3, cands.length));
    const pick = top[(rnd() * top.length) | 0];
    this.pickup = { c: pick.c, r: pick.r, level: pick.level };
  }

  /** World spot of the bonus pickup (or null if none / already taken). */
  pickupSpot(): { x: number; z: number; level: number } | null {
    if (!this.pickup || this.pickupTaken) return null;
    return { x: this.cellCenterX(this.pickup.c), z: this.cellCenterZ(this.pickup.r), level: this.pickup.level };
  }
  collectPickup(): void { this.pickupTaken = true; }
  isPickupTaken(): boolean { return this.pickupTaken; }

  private carve(a: { c: number; r: number }, b: { c: number; r: number }, rnd: () => number, straight = false): void {
    let c = a.c, r = a.r, guard = 0;
    while ((c !== b.c || r !== b.r) && guard++ < 4000) {
      this.solid[c][r] = false; this.wedge[c][r] = null; this.water.delete(this.key(c, r)); // a carved corridor is open ground
      const dc = Math.sign(b.c - c), dr = Math.sign(b.r - r);
      if (dc !== 0 && (dr === 0 || straight || rnd() < 0.6)) c += dc;
      else if (dr !== 0) r += dr;
      else c += dc;
    }
    this.solid[b.c][b.r] = false; this.wedge[b.c][b.r] = null; this.water.delete(this.key(b.c, b.r));
  }

  /** Carve meandering WATER rivers. Each is PROVISIONAL — carved, then re-checked against exit
   *  reachability and reverted if it would soft-lock the floor (so the layer is always solvable). */
  private buildRivers(layer: number, rnd: () => number): void {
    if (layer < 3) return;
    const rivers = Math.min(1 + Math.floor(layer / 20), 8); // more on bigger floors (capped — each is ~3 cells wide now)
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    const placeable = (c: number, r: number): boolean =>
      this.inGrid(c, r) && !this.keepClear.has(this.key(c, r)) && !this.holeCell.has(this.key(c, r)) &&
      !this.noNav.has(this.key(c, r)) && !this.lift[c][r];
    for (let i = 0; i < rivers; i++) {
      let cc = 0, cr = 0, found = false;
      for (let t = 0; t < 40 && !found; t++) {
        const c = 1 + ((rnd() * (this.cols - 2)) | 0), r = 1 + ((rnd() * (this.rows - 2)) | 0);
        if (placeable(c, r)) { cc = c; cr = r; found = true; }
      }
      if (!found) continue;
      const path: Array<{ c: number; r: number }> = [{ c: cc, r: cr }];
      let dir = dirs[(rnd() * 4) | 0];
      const len = Math.max(5, Math.round(0.55 * (this.cols + this.rows)));
      for (let step = 0; step < len; step++) {
        const order = rnd() < 0.55 ? [dir, ...dirs] : [...dirs]; // meander: bias straight, often turn
        let moved = false;
        for (const d of order) {
          const nc = cc + d[0], nr = cr + d[1];
          if (!placeable(nc, nr)) continue;
          cc = nc; cr = nr; dir = d; path.push({ c: nc, r: nr }); moved = true; break;
        }
        if (!moved) break;
      }
      if (path.length < 3) continue;
      // thicken the 1-wide walk into a river with organic banks (≈2–3 cells, occasional
      // wider pools) so the shoreline gradient has room to read. The carve→validate→revert
      // below still guards solvability over the WHOLE widened set; generate()'s corridor
      // carve is the second net.
      const cellSet = new Set<number>();
      const cells: Array<{ c: number; r: number }> = [];
      const addWater = (c: number, r: number) => {
        if (!placeable(c, r)) return;
        const k = this.key(c, r);
        if (cellSet.has(k)) return;
        cellSet.add(k); cells.push({ c, r });
      };
      for (const cell of path) addWater(cell.c, cell.r);
      for (const cell of [...cells]) for (const d of dirs) if (rnd() < 0.55) addWater(cell.c + d[0], cell.r + d[1]); // banks → ≈3 wide
      for (const cell of [...cells]) for (const d of dirs) if (rnd() < 0.14) addWater(cell.c + d[0], cell.r + d[1]); // occasional pools
      // provisional carve → validate → revert
      const undo: Array<{ k: number; c: number; r: number; solid: boolean; wedge: number | null }> = [];
      for (const cell of cells) {
        const k = this.key(cell.c, cell.r);
        if (this.water.has(k)) continue;
        undo.push({ k, c: cell.c, r: cell.r, solid: this.solid[cell.c][cell.r], wedge: this.wedge[cell.c][cell.r] });
        this.water.add(k); this.solid[cell.c][cell.r] = false; this.wedge[cell.c][cell.r] = null;
      }
      this.computeReach();
      if (!this.exitReachable()) for (const u of undo) { this.water.delete(u.k); this.solid[u.c][u.r] = u.solid; this.wedge[u.c][u.r] = u.wedge; }
    }
  }

  /** Advance the animated water surface (call per frame). Shared material → one uniform write. */
  tickWater(dt: number): void { waterTime.value += dt / 6; } // very gentle, slow-moving water

  /** A random cell that already has a deck at `level` (a junction point for a
   *  road one level higher), or null if none exists yet. */
  private randomDeckCell(level: number, rnd: () => number): { c: number; r: number } | null {
    const opts: Array<{ c: number; r: number }> = [];
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) if (this.decks[c][r].includes(level)) opts.push({ c, r });
    if (!opts.length) return null;
    return opts[(rnd() * opts.length) | 0];
  }

  /**
   * Build one winding road at `level` as a self-avoiding walk. `start` (a cell on
   * a level-(level-1) road) seeds a junction lift between the two levels; level-1
   * roads start at a random open ground cell. Both ends get a lift down so the
   * road is always reachable.
   */
  private buildRoad(level: number, rnd: () => number, start: { c: number; r: number } | null): void {
    let cur: { c: number; r: number };
    if (start) cur = start;
    else {
      // pick an open-ish random start cell for a ground-rooted road
      let pick: { c: number; r: number } | null = null;
      for (let t = 0; t < 60 && !pick; t++) {
        const c = 1 + ((rnd() * (this.cols - 2)) | 0), r = 1 + ((rnd() * (this.rows - 2)) | 0);
        if (this.keepClear.has(this.key(c, r)) || this.decks[c][r].length) continue;
        pick = { c, r };
      }
      if (!pick) return;
      cur = pick;
    }
    const path: Array<{ c: number; r: number }> = [];
    this.addDeck(cur.c, cur.r, level); path.push({ ...cur });
    const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    let dir = dirs[(rnd() * 4) | 0];
    const len = 4 + ((rnd() * 6) | 0) + ((this.cols + this.rows) >> 4); // longer roads on bigger floors
    for (let step = 0; step < len; step++) {
      // bias toward continuing straight; otherwise turn
      const order = rnd() < 0.6 ? [dir, ...dirs] : [...dirs];
      let moved = false;
      for (const d of order) {
        const nc = cur.c + d[0], nr = cur.r + d[1];
        if (!this.inGrid(nc, nr)) continue;
        if (this.keepClear.has(this.key(nc, nr))) continue;
        if (this.decks[nc][nr].includes(level)) continue; // don't overlap this level
        cur = { c: nc, r: nr }; dir = d; moved = true;
        this.addDeck(cur.c, cur.r, level); path.push({ ...cur });
        break;
      }
      if (!moved) break;
    }
    if (path.length < 2) return;
    // lifts: the start (junction to the level below) + the far end (ground shaft).
    // If either is refused for spacing, abandon the road (remove its deck) so no
    // stranded/unreachable decks remain.
    const a = path[0], b = path[path.length - 1];
    const aOk = this.placeLift(a.c, a.r, start ? level - 1 : 0, level);
    const bOk = this.placeLift(b.c, b.r, 0, level);
    if (!aOk || !bOk) {
      for (const cell of path) this.decks[cell.c][cell.r] = this.decks[cell.c][cell.r].filter((L) => L !== level);
      if (aOk) { this.lift[a.c][a.r] = null; this.placedLifts.delete(this.key(a.c, a.r)); }
      if (bOk) { this.lift[b.c][b.r] = null; this.placedLifts.delete(this.key(b.c, b.r)); }
    }
  }

  private placeLift(c: number, r: number, lo: number, hi: number): boolean {
    if (!this.isLiftSpaced(c, r)) return false; // refuse if it would cluster
    this.lift[c][r] = { lo, hi };
    this.placedLifts.add(this.key(c, r));
    if (lo === 0) { this.solid[c][r] = false; this.wedge[c][r] = null; this.water.delete(this.key(c, r)); } // a ground lift pad must be drivable-to
    return true;
  }

  /** Surface-graph distance from the spawn landing zone to every (cell, level).
   *  Seeds from the spawn hole's clear landing cells (the player drives out onto
   *  them). Used for reachability + the pickup's "hardness". */
  private surfaceDist(seed: Array<{ c: number; r: number }>): number[][][] {
    const dist: number[][][] = [];
    for (let L = 0; L <= TOP_LEVEL; L++) { dist[L] = []; for (let c = 0; c < this.cols; c++) { dist[L][c] = []; for (let r = 0; r < this.rows; r++) dist[L][c][r] = Infinity; } }
    const q: Array<[number, number, number]> = [];
    for (const s of seed) {
      if (this.valid(s.c, s.r, 0) && dist[0][s.c][s.r] === Infinity) { dist[0][s.c][s.r] = 0; q.push([s.c, s.r, 0]); }
    }
    for (let head = 0; head < q.length; head++) { // O(1) dequeue via head index (not O(n) shift)
      const [c, r, L] = q[head];
      const d = dist[L][c][r];
      for (const o of ORTHO) {
        const nc = c + o[0], nr = r + o[1];
        if (this.valid(nc, nr, L) && dist[L][nc][nr] > d + 1) { dist[L][nc][nr] = d + 1; q.push([nc, nr, L]); }
      }
      const lf = this.lift[c][r];
      if (lf) {
        const o = lf.lo === L ? lf.hi : lf.hi === L ? lf.lo : -1;
        if (o >= 0 && this.valid(c, r, o) && dist[o][c][r] > d + LIFT_COST) { dist[o][c][r] = d + LIFT_COST; q.push([c, r, o]); }
      }
    }
    return dist;
  }

  private computeReach(): void {
    this.genDist = this.surfaceDist(this.spawnLanding);
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) this.reach[c][r] = isFinite(this.genDist[0][c][r]);
  }

  /** Strip elevated decks that aren't reachable on the surface graph, then drop
   *  any lift left pointing at the removed deck. Reachable lifts keep their (also
   *  reachable) decks, so this never strands the player — it only clears leftovers. */
  private pruneOrphanDecks(): void {
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      if (!this.decks[c][r].length) continue;
      this.decks[c][r] = this.decks[c][r].filter((L) => isFinite(this.genDist[L]?.[c]?.[r] ?? Infinity));
    }
    this.validateLifts(); // a lift whose deck just vanished is now invalid (and was unreachable)
  }

  /** The exit hole is reachable iff one of its landing cells is. */
  private exitReachable(): boolean {
    for (const cell of this.exitLanding) if (this.reach[cell.c]?.[cell.r]) return true;
    return false;
  }

  // ---- meshes -----------------------------------------------------------

  private buildMeshes(): void {
    // gather per-kind instance transforms, then emit one InstancedMesh per kind.
    // Decks are grouped per LEVEL (and per road/lift material) so each transparent
    // instanced draw is coplanar — no inter-instance depth-sort artifacts.
    const solids: THREE.Matrix4[] = [];
    const wedges: THREE.Matrix4[][] = [[], [], [], []]; // by orientation 0-3
    const deckPos: THREE.Matrix4[][] = [[], [], [], []]; // by level 1..TOP_LEVEL
    const liftDeckPos: THREE.Matrix4[][] = [[], [], [], []];
    const pads: THREE.Matrix4[] = [];
    const posts: THREE.Matrix4[] = [];
    const e = CELL / 2 - 2; // post inset from the cell edge

    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      const x = this.cellCenterX(c), z = this.cellCenterZ(r);
      if (this.solid[c][r]) {
        const wk = this.wedge[c][r];
        if (wk !== null) { // a sloped wedge block (triangle prism); collision carries the orientation
          wedges[wk].push(new THREE.Matrix4().makeTranslation(x, 0, z));
          this.walls.push({ minX: x - CELL / 2, maxX: x + CELL / 2, minZ: z - CELL / 2, maxZ: z + CELL / 2, wedge: wk });
        } else {
          solids.push(new THREE.Matrix4().makeTranslation(x, BLOCK_H / 2, z));
          this.walls.push({ minX: x - CELL / 2, maxX: x + CELL / 2, minZ: z - CELL / 2, maxZ: z + CELL / 2 });
        }
      }
      if (this.water.has(this.key(c, r))) { // a river cell: blocks tanks, shells fly over
        this.walls.push({ minX: x - CELL / 2, maxX: x + CELL / 2, minZ: z - CELL / 2, maxZ: z + CELL / 2, water: true });
      }
      const lf = this.lift[c][r];
      for (const L of this.decks[c][r]) {
        // green (lift) colour ONLY on the deck levels this lift actually connects —
        // a road deck merely passing through a lift cell stays road-coloured.
        const isLiftDeck = !!lf && (L === lf.hi || (L === lf.lo && lf.lo >= 1));
        (isLiftDeck ? liftDeckPos : deckPos)[L].push(new THREE.Matrix4().makeTranslation(x, L * LEVEL_H - DECK_T / 2, z));
      }
      if (lf) {
        const loY = lf.lo * LEVEL_H, hiY = lf.hi * LEVEL_H;
        if (lf.lo === 0) pads.push(new THREE.Matrix4().makeTranslation(x, 0.75, z));
        for (const [sx, sz] of [[-e, -e], [e, -e], [-e, e], [e, e]] as const) {
          posts.push(new THREE.Matrix4().makeScale(2, hiY - loY, 2).setPosition(x + sx, (loY + hiY) / 2, z + sz));
        }
      }
    }

    addInstanced(this.group, SOLID_GEOM, obstacleMat, solids, true, true);
    for (let k = 0; k < 4; k++) addInstanced(this.group, WEDGE_GEOM[k], obstacleMat, wedges[k], true, true);
    this.emitWater(); // merged, shoreline-graded water surface (its own per-vertex geometry)
    for (let L = 1; L <= TOP_LEVEL; L++) {
      // elevated roads draw ABOVE the ground-decal ladder (treads/death-marks/water are −8..−6) but
      // still BELOW every UI overlay (aim line/rings/labels are ≥2), and a higher level draws over a
      // lower one (the +L·0.1 term) — so overpasses correctly occlude what's beneath them.
      const ro = L * 0.1 - 4;
      addInstanced(this.group, DECK_GEOM, deckMats[Math.min(L - 1, deckMats.length - 1)], deckPos[L], false, true, ro);
      addInstanced(this.group, DECK_GEOM, liftDeckMat, liftDeckPos[L], false, true, ro);
    }
    addInstanced(this.group, PAD_GEOM, liftPadMat, pads, false, true);
    addInstanced(this.group, POST_GEOM, postMat, posts, false, false);
  }

  /** Build the water surface as ONE merged mesh whose vertices carry a per-corner
   *  distance-to-shore (`aShore`, world studs). A multi-source BFS rings the water
   *  outward from land; corner depth = average of the 4 touching cells (land sits at
   *  −½ cell, so the waterline lands exactly on the land/water boundary). The shader
   *  bands the colour by that depth → a cartoon shoreline gradient that follows the
   *  river's bends. Cheap (≈one quad per water cell) and rebuilt per layer. */
  private emitWater(): void {
    this.waterGeom = null;
    if (this.water.size === 0) return;
    const isW = (c: number, r: number) => this.inGrid(c, r) && this.water.has(this.key(c, r));
    // 1) ring distance (in cells) outward from the shore — BFS seeded on land-adjacent water
    const D = new Map<number, number>();
    const q: number[] = [];
    for (const k of this.water) {
      const c = Math.floor(k / this.rows), r = k % this.rows;
      for (const o of ORTHO) if (!isW(c + o[0], r + o[1])) { D.set(k, 1); q.push(k); break; }
    }
    for (let h = 0; h < q.length; h++) {
      const k = q[h], c = Math.floor(k / this.rows), r = k % this.rows, d = D.get(k)!;
      for (const o of ORTHO) {
        const nc = c + o[0], nr = r + o[1];
        if (!isW(nc, nr)) continue;
        const nk = this.key(nc, nr);
        if (!D.has(nk)) { D.set(nk, d + 1); q.push(nk); }
      }
    }
    // 2) per-cell depth in studs (land/out-of-grid = −½ cell, above the surface)
    const HALF = CELL / 2;
    const cellDepth = (c: number, r: number) => (isW(c, r) ? (D.get(this.key(c, r))! - 0.5) * CELL : -HALF);
    // 3) corner depth = mean of the 4 cells meeting at that grid node (smooth, SIGNED: outer
    //    corners go negative so the shader's aShore≈0 isocontour rounds convex corners)
    const corner = (i: number, j: number) =>
      (cellDepth(i - 1, j - 1) + cellDepth(i, j - 1) + cellDepth(i - 1, j) + cellDepth(i, j)) * 0.25;
    // 4) one fan per water cell (4 corners + a CENTER vertex). The centre carries the cell's
    //    TRUE depth so thin 1-cell rivers keep a solid, deep middle even though all their corners
    //    sit on the shore (≈0) — which lets the shader feather EVERY edge inward without thin
    //    features dissolving. +Y-facing (material is DoubleSide, so winding doesn't matter).
    const pos: number[] = [], nor: number[] = [], shore: number[] = [], idx: number[] = [];
    const Y = 0.5; let v = 0; const wo: [number, number] = [0, 0];
    for (const k of this.water) {
      const c = Math.floor(k / this.rows), r = k % this.rows;
      const x0 = this.gx0 + c * CELL, x1 = x0 + CELL, z0 = this.gz0 + r * CELL, z1 = z0 + CELL;
      const xm = (x0 + x1) / 2, zm = (z0 + z1) / 2;
      // wobble each corner off the grid (per-node ⇒ shared corners agree ⇒ watertight)
      waterWobble(x0, z0, wo); const ax = x0 + wo[0], az = z0 + wo[1];
      waterWobble(x1, z0, wo); const bx = x1 + wo[0], bz = z0 + wo[1];
      waterWobble(x1, z1, wo); const cx = x1 + wo[0], cz = z1 + wo[1];
      waterWobble(x0, z1, wo); const dx = x0 + wo[0], dz = z1 + wo[1];
      waterWobble(xm, zm, wo); const mx = xm + wo[0], mz = zm + wo[1];
      pos.push(ax, Y, az, bx, Y, bz, cx, Y, cz, dx, Y, dz, mx, Y, mz);
      nor.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
      shore.push(corner(c, r), corner(c + 1, r), corner(c + 1, r + 1), corner(c, r + 1), cellDepth(c, r));
      idx.push(v, v + 1, v + 4, v + 1, v + 2, v + 4, v + 2, v + 3, v + 4, v + 3, v, v + 4); // fan around the centre (v+4)
      v += 5;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
    g.setAttribute("aShore", new THREE.Float32BufferAttribute(shore, 1));
    g.setIndex(idx);
    this.waterGeom = g;
    const mesh = new THREE.Mesh(g, waterMat);
    mesh.castShadow = false; mesh.receiveShadow = false; mesh.frustumCulled = false;
    mesh.renderOrder = -6; // ground-decal ladder: above treads/death-marks, below elevated roads
    this.group.add(mesh);
  }

  // ---- height + lift queries -------------------------------------------

  /** World-local height of a level's surface (feet height). */
  levelHeight(level: number): number { return level * LEVEL_H; }

  /** Foot-rest height above the ground at world (x,z): a solid maze block's top (BLOCK_H) if
   *  one is here, else 0. Lets a mecha's legs rest ON boxes instead of planting through them. */
  surfaceTop(x: number, z: number): number {
    const { c, r } = this.cellOf(x, z);
    return this.inGrid(c, r) && this.solid[c][r] ? BLOCK_H : 0;
  }

  /** Is the ground cell under world (x,z) reachable from spawn on the surface graph?
   *  A reachable cell is open, connected, and (since neighbours are whole open cells)
   *  footprint-traversable — so a tank dropped here can always drive out. Spawn-vetting
   *  uses this so nothing lands in a walled-off pocket. */
  isReachable(x: number, z: number): boolean {
    const { c, r } = this.cellOf(x, z);
    return this.inGrid(c, r) && this.reach[c]?.[r] === true;
  }

  /** The lift at (x,z) IF it connects `level` (so a tank on `level` can ride it),
   *  with the level it would take you to; else null. */
  liftAt(x: number, z: number, level: number): { toLevel: number } | null {
    const { c, r } = this.cellOf(x, z);
    const lf = this.lift[c][r];
    if (!lf) return null;
    if (lf.lo === level) return { toLevel: lf.hi };
    if (lf.hi === level) return { toLevel: lf.lo };
    return null;
  }
  /** Center of the cell containing world (x,z) — to snap a tank onto a lift. */
  cellCenter(x: number, z: number, out: { x: number; z: number }): void {
    const { c, r } = this.cellOf(x, z);
    out.x = this.cellCenterX(c); out.z = this.cellCenterZ(r);
  }

  /** Confine a road tank (level≥1) to its deck — push it out of cells that have
   *  no deck at its level (the road edges act as railings). Ground tanks (0) are
   *  handled by gamemap's solid-block collision. */
  confine(pos: { x: number; z: number }, r: number, level: number): void {
    if (level <= 0) return;
    const cell = this.cellOf(pos.x, pos.z);
    for (let pass = 0; pass < 2; pass++) {
      for (let dc = -1; dc <= 1; dc++) for (let dr = -1; dr <= 1; dr++) {
        const c = cell.c + dc, rr = cell.r + dr;
        if (!this.inGrid(c, rr) || this.decks[c][rr].includes(level)) continue;
        pushOut(pos, r, this.cellCenterX(c), this.cellCenterZ(rr));
      }
    }
  }

  /** The highest deck level present at world (x,z) that is ≤ `fromLevel` — i.e. the level a tank
   *  standing here actually rests on (0 = ground if no deck applies). Roads have NO railings now,
   *  so when a tank drives OFF a deck edge it STEPS DOWN to whatever deck/ground is beneath it. */
  supportLevel(x: number, z: number, fromLevel: number): number {
    if (fromLevel <= 0) return 0;
    const { c, r } = this.cellOf(x, z);
    if (!this.inGrid(c, r)) return 0;
    let best = 0;
    for (const L of this.decks[c][r]) if (L <= fromLevel && L > best) best = L;
    return best;
  }

  /** Reflect a road shell (level≥1) off its road edges. Ground shells (0) bounce
   *  off solid blocks via gamemap. Returns true if it bounced. */
  reflectShell(pos: { x: number; z: number }, vel: { x: number; z: number }, r: number, level: number): boolean {
    if (level <= 0) return false;
    const cell = this.cellOf(pos.x, pos.z);
    let bounced = false;
    for (let dc = -1; dc <= 1; dc++) for (let dr = -1; dr <= 1; dr++) {
      const c = cell.c + dc, rr = cell.r + dr;
      if (!this.inGrid(c, rr) || this.decks[c][rr].includes(level)) continue;
      if (reflectOff(pos, vel, r, this.cellCenterX(c), this.cellCenterZ(rr))) bounced = true;
    }
    return bounced;
  }

  // ---- enemy navigation -------------------------------------------------

  spawnPoints(count: number, avoid: { x: number; z: number }, half?: { entryX: number; entryZ: number; exitX: number; exitZ: number }): THREE.Vector2[] {
    const open: Array<{ c: number; r: number }> = [];
    for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) {
      if (!this.valid(c, r, 0) || !this.reach[c][r]) continue;
      const x = this.cellCenterX(c), z = this.cellCenterZ(r);
      if (Math.hypot(x - avoid.x, z - avoid.z) < 95) continue;
      if (half) { // CARD MODE: enemy half only (cell nearer the exit hole than the entry) — exact complement of validCardCell
        const de = (x - half.entryX) ** 2 + (z - half.entryZ) ** 2;
        const dx = (x - half.exitX) ** 2 + (z - half.exitZ) ** 2;
        if (de <= dx) continue;
      }
      open.push({ c, r });
    }
    // seeded shuffle so a resumed floor places enemies identically
    const srnd = rng(this.spawnSeed);
    for (let i = open.length - 1; i > 0; i--) { const j = (srnd() * (i + 1)) | 0; [open[i], open[j]] = [open[j], open[i]]; }
    return open.slice(0, count).map((cell) => new THREE.Vector2(this.cellCenterX(cell.c), this.cellCenterZ(cell.r)));
  }

  /** Valid source cells for a set of world positions (nearest-valid fallback). */
  private sourceCells(srcs: ReadonlyArray<{ x: number; z: number; level: number }>): Array<[number, number, number]> {
    const out: Array<[number, number, number]> = [];
    for (const s of srcs) {
      const g = this.cellOf(s.x, s.z);
      if (this.valid(g.c, g.r, s.level)) { out.push([g.c, g.r, s.level]); continue; }
      const open = this.nearestValid(g, s.level) ?? this.nearestValid(g, 0);
      if (open) out.push([open.c, open.r, open.L]);
    }
    return out;
  }

  /** Hash of the source (cell,level) set — rebuild the field only when it changes. */
  private flowKey(seeds: Array<[number, number, number]>): number {
    let k = seeds.length;
    for (const s of seeds) k = (k * 131 + (this.key(s[0], s[1]) * (TOP_LEVEL + 1) + s[2])) | 0;
    return k;
  }

  /** Multi-source surface BFS into `field`: dist 0 at every seed, then flood
   *  (4-neighbour + lift edges). O(1) dequeue via head index. `avoidCurbs` treats the
   *  hole+curb box as impassable — used ONLY for the mercenary (ally) flow so mercs
   *  never path onto the elevator (the flow otherwise ignores the curb walls and routes
   *  straight through). The enemy flow keeps the looser rule (enemies chase the moving
   *  player and never settle, so they don't need it — and it preserves their behaviour). */
  private buildFlow(field: number[][][], seeds: Array<[number, number, number]>, avoidCurbs = false): void {
    for (let L = 0; L <= TOP_LEVEL; L++) for (let c = 0; c < this.cols; c++) for (let r = 0; r < this.rows; r++) field[L][c][r] = Infinity;
    const q: Array<[number, number, number]> = [];
    for (const s of seeds) { if (field[s[2]][s[0]][s[1]] !== 0) { field[s[2]][s[0]][s[1]] = 0; q.push(s); } }
    for (let head = 0; head < q.length; head++) {
      const [c, r, L] = q[head];
      const d = field[L][c][r];
      for (const o of ORTHO) {
        const nc = c + o[0], nr = r + o[1];
        if (avoidCurbs && L === 0 && this.noNav.has(this.key(nc, nr))) continue;
        if (this.valid(nc, nr, L) && field[L][nc][nr] > d + 1) { field[L][nc][nr] = d + 1; q.push([nc, nr, L]); }
      }
      const lf = this.lift[c][r];
      if (lf) {
        const other = lf.lo === L ? lf.hi : lf.hi === L ? lf.lo : -1;
        if (other >= 0 && this.valid(c, r, other) && field[other][c][r] > d + LIFT_COST) { field[other][c][r] = d + LIFT_COST; q.push([c, r, other]); }
      }
    }
  }

  /** Rebuild the ENEMY flow field toward the threats enemies hunt (player + mercenaries). */
  updateFlow(threats: ReadonlyArray<{ x: number; z: number; level: number }>): void {
    const seeds = this.sourceCells(threats);
    const key = this.flowKey(seeds);
    if (key === this.lastGoal) return;
    this.lastGoal = key;
    this.buildFlow(this.dist, seeds);
  }

  /** Rebuild the ALLY flow field toward the enemies mercenaries hunt. Throttled to
   *  at most once every few frames: there can be ~18 enemy seeds (vs 1-2 for the
   *  enemy flow), so the cheap change-detection key flips almost every frame in a
   *  crowd and would otherwise re-flood the whole grid each tick. Merc pathing
   *  tolerates a few frames of staleness, so this caps the worst-case BFS cost. */
  updateAllyFlow(targets: ReadonlyArray<{ x: number; z: number; level: number }>): void {
    if (this.allyFlowCooldown > 0) { this.allyFlowCooldown--; return; }
    const seeds = this.sourceCells(targets);
    const key = this.flowKey(seeds);
    if (key === this.lastAllyGoal) return;
    this.lastAllyGoal = key;
    this.buildFlow(this.adist, seeds, true); // mercs avoid the elevator pits + curbs
    this.allyFlowCooldown = 2;
  }

  private nearestValid(cell: { c: number; r: number }, L: number): { c: number; r: number; L: number } | null {
    for (let rad = 0; rad < Math.max(this.cols, this.rows); rad++) {
      for (let dc = -rad; dc <= rad; dc++) for (let dr = -rad; dr <= rad; dr++) {
        if (rad > 0 && Math.abs(dc) !== rad && Math.abs(dr) !== rad) continue; // scan the new ring only — O(R²) not O(R³)
        const c = cell.c + dc, r = cell.r + dr;
        if (this.valid(c, r, L)) return { c, r, L };
      }
    }
    return null;
  }

  /** Enemy steering at (x,z,level): toward the nearest threat (player/mercenary). */
  flowStep(x: number, z: number, level: number, out: FlowStep): void { this.stepOn(this.dist, x, z, level, out); }
  /** Mercenary steering at (x,z,level): toward the nearest enemy. */
  allyFlowStep(x: number, z: number, level: number, out: FlowStep): void { this.stepOn(this.adist, x, z, level, out); }

  /** Ally kiter steering AWAY from the enemies: same uphill-gradient flee as fleeStep,
   *  but on the ALLY flow (distance-from-enemies) so an ally backs away from foes. */
  allyFleeStep(x: number, z: number, level: number, out: FlowStep): void { this.fleeOn(this.adist, x, z, level, out); }

  /** Enemy steering AWAY from the threats (kite/flee): climb the enemy flow's distance
   *  gradient UPHILL toward the farthest reachable neighbour, so a fleeing tank routes
   *  through the maze instead of backing straight into a wall. Same-level only. */
  fleeStep(x: number, z: number, level: number, out: FlowStep): void { this.fleeOn(this.dist, x, z, level, out); }

  /** Shared flee: climb `field`'s distance gradient uphill (away from its seeds). */
  private fleeOn(field: number[][][], x: number, z: number, level: number, out: FlowStep): void {
    const { c, r } = this.cellOf(x, z);
    const cur = field[level]?.[c]?.[r] ?? Infinity;
    let bestD = isFinite(cur) ? cur : -Infinity; // stranded on a curb? any finite neighbour beats it
    let move: { c: number; r: number } | null = null;
    for (const ev of FLOW_NB) {
      const nc = c + ev[0], nr = r + ev[1];
      if (!this.valid(nc, nr, level)) continue;
      const diag = ev[0] !== 0 && ev[1] !== 0;
      if (diag && !(this.valid(c + ev[2], r + ev[3], level) && this.valid(c, nr, level))) continue; // no corner-cut
      const d = field[level][nc][nr];
      if (isFinite(d) && d > bestD) { bestD = d; move = { c: nc, r: nr }; }
    }
    if (move) {
      out.kind = "move"; out.toLevel = level;
      out.dx = this.cellCenterX(move.c) - x; out.dz = this.cellCenterZ(move.r) - z;
      const len = Math.hypot(out.dx, out.dz) || 1; out.dx /= len; out.dz /= len;
      return;
    }
    out.kind = "none"; out.dx = 0; out.dz = 0; out.toLevel = level;
  }

  /** Steer down a flow `field`: move toward the best neighbour cell, ride a lift,
   *  or nothing (no route). */
  private stepOn(field: number[][][], x: number, z: number, level: number, out: FlowStep): void {
    const { c, r } = this.cellOf(x, z);
    let bestD = field[level]?.[c]?.[r] ?? Infinity;
    let move: { c: number; r: number } | null = null;
    let liftTo = -1;
    // horizontal (8-dir, but don't cut a corner across a missing deck)
    for (const ev of FLOW_NB) {
      const nc = c + ev[0], nr = r + ev[1];
      if (!this.valid(nc, nr, level)) continue;
      // diagonal: require both shared orthogonal cells to be valid (no corner cut)
      const diag = ev[0] !== 0 && ev[1] !== 0;
      if (diag && !(this.valid(c + ev[2], r + ev[3], level) && this.valid(c, nr, level))) continue;
      const d = field[level][nc][nr];
      if (d < bestD) { bestD = d; move = { c: nc, r: nr }; liftTo = -1; }
    }
    // lift edge (change level at this cell)
    const lf = this.lift[c][r];
    if (lf) {
      const other = lf.lo === level ? lf.hi : lf.hi === level ? lf.lo : -1;
      if (other >= 0) {
        const d = field[other]?.[c]?.[r] ?? Infinity;
        if (d < bestD) { bestD = d; liftTo = other; move = null; }
      }
    }
    if (liftTo >= 0) { out.kind = "lift"; out.toLevel = liftTo; out.dx = 0; out.dz = 0; return; }
    if (move) {
      out.kind = "move"; out.toLevel = level;
      out.dx = this.cellCenterX(move.c) - x; out.dz = this.cellCenterZ(move.r) - z;
      const len = Math.hypot(out.dx, out.dz) || 1; out.dx /= len; out.dz /= len;
      return;
    }
    out.kind = "none"; out.dx = 0; out.dz = 0; out.toLevel = level;
  }

  dispose(): void {
    this.waterGeom?.dispose(); // the only per-layer geometry (others are shared module-level)
    this.group.removeFromParent();
  }
}

/** Emit one InstancedMesh for a set of transforms (skips empty sets). Shared
 *  geometry + one draw call replaces a mesh-per-cell (huge draw-call + GC win). */
function addInstanced(group: THREE.Group, geom: THREE.BufferGeometry, mat: THREE.Material, mats: THREE.Matrix4[], cast: boolean, receive: boolean, renderOrder = 0): void {
  if (!mats.length) return;
  const im = new THREE.InstancedMesh(geom, mat, mats.length);
  for (let i = 0; i < mats.length; i++) im.setMatrixAt(i, mats[i]);
  im.instanceMatrix.needsUpdate = true;
  im.castShadow = cast;
  im.receiveShadow = receive;
  im.frustumCulled = false; // the arena hugs the camera; never cull it wholesale
  im.renderOrder = renderOrder; // transparent decks: order by level so higher roads draw over lower
  group.add(im);
}

/** Push a circle out of a 40×40 cell box centered at (bx,bz). */
function pushOut(pos: { x: number; z: number }, r: number, bx: number, bz: number): void {
  const minX = bx - CELL / 2, maxX = bx + CELL / 2, minZ = bz - CELL / 2, maxZ = bz + CELL / 2;
  const cx = Math.max(minX, Math.min(pos.x, maxX));
  const cz = Math.max(minZ, Math.min(pos.z, maxZ));
  const dx = pos.x - cx, dz = pos.z - cz, d2 = dx * dx + dz * dz;
  if (d2 >= r * r) return;
  if (d2 > 1e-6) { const d = Math.sqrt(d2); pos.x = cx + (dx / d) * r; pos.z = cz + (dz / d) * r; }
  else {
    const toL = pos.x - minX, toR = maxX - pos.x, toN = pos.z - minZ, toS = maxZ - pos.z;
    const m = Math.min(toL, toR, toN, toS);
    if (m === toL) pos.x = minX - r; else if (m === toR) pos.x = maxX + r; else if (m === toN) pos.z = minZ - r; else pos.z = maxZ + r;
  }
}

/** Reflect a moving circle off a 40×40 cell box. Returns true if it bounced. */
function reflectOff(pos: { x: number; z: number }, vel: { x: number; z: number }, r: number, bx: number, bz: number): boolean {
  const minX = bx - CELL / 2, maxX = bx + CELL / 2, minZ = bz - CELL / 2, maxZ = bz + CELL / 2;
  const cx = Math.max(minX, Math.min(pos.x, maxX));
  const cz = Math.max(minZ, Math.min(pos.z, maxZ));
  if ((pos.x - cx) ** 2 + (pos.z - cz) ** 2 >= r * r) return false;
  const overlapX = Math.min(pos.x - minX, maxX - pos.x);
  const overlapZ = Math.min(pos.z - minZ, maxZ - pos.z);
  const insideX = pos.x > minX && pos.x < maxX, insideZ = pos.z > minZ && pos.z < maxZ;
  const flipX = insideX && !insideZ ? false : insideZ && !insideX ? true : overlapX < overlapZ;
  if (flipX) { vel.x = -vel.x; pos.x = vel.x > 0 ? maxX + r : minX - r; }
  else { vel.z = -vel.z; pos.z = vel.z > 0 ? maxZ + r : minZ - r; }
  return true;
}
